// hooks/useCvSkill.ts
import { useContext, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import CvSkillDto from "../api/models/cvskill"
import User from "../api/models/user"
import UserCvSkillDto from "../api/models/usercvskill"
import InternStatus from "../api/models/internStatus"
import cvskillService from "../api/services/cvskillService"
import UserContext from "../contexts/user"

export function useCvSkill() {
	const [cvSkill, setCvSkill] = useState<CvSkillDto | null>(null)
	// ✅ Utiliser le type User complet (avec toutes les propriétés)
	// Stocker un `User` complet côté hook pour que les composants qui
	// consomment `user` n'aient pas d'erreurs de type/accès.
	const [user, setUser] = useState<User | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const location = useLocation()
	const { user: currentUser } = useContext(UserContext)

	// 2 - TRACE: `currentUser` provient du `UserContext` fourni par `UserProvider`.
	//     Ce log permet de vérifier que le contexte contient bien l'utilisateur attendu
	//     (token, id, email, firstName, name, etc.).
	console.log("[2][useCvSkill] currentUser (from UserContext) =", currentUser)

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true)
				const nav: any = location.state || {}

				// 🔍 Récupérer le userId (priorité: state > localStorage > currentUser)
				const stateUserId = nav?.userId ?? null
				const lsUserId = localStorage.getItem("selectedUserId")
				const effectiveUserId =
					stateUserId ??
					(lsUserId ? Number(lsUserId) : currentUser?.id)

				console.log("[useCvSkill] effectiveUserId =", effectiveUserId)

				if (!effectiveUserId) {
					console.warn("[useCvSkill] Aucun userId trouvé")
					setError("ID utilisateur introuvable")
					setLoading(false)
					return
				}

				// ✅ Utiliser l'endpoint sécurisé /user/{userId}
				console.log(
					`[useCvSkill] Récupération du CV Skill pour userId: ${effectiveUserId}`,
				)

				const cv = await cvskillService.getCvSkillsByUserId(
					Number(effectiveUserId),
				)

				console.log("[useCvSkill] cvSkill récupéré =", cv)
				setCvSkill(cv)

				// 2.2 - IMPORTANT: Le CV contient un objet `user` (UserCvSkillDto) qui
				//       correspond aux informations personnelles affichées dans les
				//       templates (nom, prénom, email, phone, dateOfBirth...).
				//       Prioriser ce `cv.user` pour alimenter `user` renvoyé par le hook
				//       afin que ThemeTwo reçoive le user correspondant au CV affiché.
				console.log("[2][useCvSkill] cv.user =", cv?.user)

				if (cv?.user) {
					console.log(
						"[2][useCvSkill] setUser with cv.user (preferred):",
						cv.user,
					)

					// Mapper UserCvSkillDto -> User (champ par champ minimal)
					const mappedUser: User = {
						email: cv.user.email ?? "",
						password: "", // unknown from CV
						roles: [],
						firstName: cv.user.firstName ?? "",
						id: cv.user.id ? String(cv.user.id) : undefined,
						name: cv.user.name ?? "",
						phone: cv.user.phone ?? "",
						postalCode: null,
						dateOfBirth: cv.user.dateOfBirth
							? new Date(cv.user.dateOfBirth)
							: new Date(),
						internStatus: new InternStatus(0, ""),
						internshipPeriod: cv.user.internshipPeriod ?? null,
						internshipStartDate: cv.user.internshipStartDate
							? new Date(cv.user.internshipStartDate)
							: undefined,
						internshipEndDate: cv.user.internshipEndDate
							? new Date(cv.user.internshipEndDate)
							: undefined,
						searchSubject: cv.user.searchSubject ?? "",
						civility: cv.user.civility ?? "",
						diploma: cv.user.diploma ?? null,
						jobs: null,
						token: "",
						cv: null,
						coverLetter: null,
						activated: false,
						createdDate: new Date(),
						lastModifiedDate: new Date(),
						profileUpdateCount: 0,
					}

					console.log("mapped -- ", mappedUser)

					setUser(mappedUser)
				} else if (currentUser) {
					// fallback: si le CV ne contient pas de user, utiliser currentUser
					console.log(
						"[2][useCvSkill] cv.user absent, fallback to currentUser:",
						currentUser,
					)

					setUser(currentUser)
				}
				// ✅ Stocker les IDs pour une utilisation future
				if (cv?.id) {
					localStorage.setItem("selectedCvSkillId", String(cv.id))
					localStorage.setItem(
						"selectedUserId",
						String(effectiveUserId),
					)
				}

				console.log("----------------------- //// user -- ", user)

				/*
				// ✅ Utiliser currentUser qui contient TOUTES les propriétés
				// (searchSubject, internshipPeriod, internshipStartDate, etc.)
				// 2.1 - Avant d'appeler setUser, on loggue pour s'assurer que c'est bien
				//       le user attendu qui va être propagé vers les composants.
				if (currentUser) {
					console.log(
						"[2][useCvSkill] Utilisateur complet depuis currentUser:",
						currentUser,
					)
					console.log(
						"[2][useCvSkill] setUser with currentUser:",
						currentUser,
					)
					setUser(currentUser)
				}
				*/
			} catch (e: any) {
				console.error("[useCvSkill] Erreur lors de la récupération:", e)

				// Gestion des erreurs spécifiques
				if (e.response?.status === 404) {
					setError("Aucun CV Skill trouvé pour cet utilisateur.")
				} else if (e.response?.status === 403) {
					setError(
						"Accès refusé. Vous ne pouvez pas accéder à ce CV.",
					)
				} else {
					setError("Impossible de récupérer les données.")
				}
			} finally {
				setLoading(false)
			}
		}
		fetchData()
	}, [location.state, currentUser])

	return { cvSkill, user, loading, error }
}

export default useCvSkill
